import { BedrockAgentCoreClient, InvokeAgentRuntimeCommand } from '@aws-sdk/client-bedrock-agentcore';
import { logger } from '../utils/logger';

export interface AWSAIAgentRequest {
  prompt: string;
  symbol: string;
  timeframe: string;
  analysis_depth: string;
}

export interface AWSAIAgentResponse {
  result: {
    comprehensive_analysis: string;
    symbols: string[];
    timeframe: string;
    analysis_depth: string;
    analysis_type: string;
    confidence: number;
    processing_time_ms: number;
    timestamp: string;
  };
}

export class AWSAIAgentService {
  private static instance: AWSAIAgentService;
  private runtimeSessionId: string | null = null;
  private sessionsByTicker: Map<string, string> = new Map(); // Store session per ticker

  private constructor() {}

  static getInstance(): AWSAIAgentService {
    if (!AWSAIAgentService.instance) {
      AWSAIAgentService.instance = new AWSAIAgentService();
    }
    return AWSAIAgentService.instance;
  }

  private generateSessionId(): string {
    // Generate a session ID with minimum 33 characters
    const timestamp = Date.now().toString();
    const randomPart1 = Math.random().toString(36).substring(2, 15);
    const randomPart2 = Math.random().toString(36).substring(2, 15);
    const sessionId = `session-${timestamp}-${randomPart1}-${randomPart2}`;
    
    // Ensure minimum 33 characters by padding if necessary
    if (sessionId.length < 33) {
      const padding = Math.random().toString(36).substring(2, 35 - sessionId.length);
      return `${sessionId}-${padding}`;
    }
    
    return sessionId;
  }

  private getCredentials() {
    const accessKeyId = import.meta.env.VITE_AWS_ACCESS_KEY_ID;
    const secretAccessKey = import.meta.env.VITE_AWS_SECRET_ACCESS_KEY;
    const sessionToken = import.meta.env.VITE_AWS_SESSION_TOKEN;

    logger.debug('AWS AI Agent - Environment check:', {
      hasAccessKeyId: !!accessKeyId,
      accessKeyIdLength: accessKeyId?.length || 0,
      hasSecretAccessKey: !!secretAccessKey,
      secretAccessKeyLength: secretAccessKey?.length || 0,
      hasSessionToken: !!sessionToken,
      sessionTokenLength: sessionToken?.length || 0
    });

    if (!accessKeyId || !secretAccessKey) {
      throw new Error('AWS credentials not configured. Please set VITE_AWS_ACCESS_KEY_ID and VITE_AWS_SECRET_ACCESS_KEY in your environment variables.');
    }

    return {
      accessKeyId,
      secretAccessKey,
      sessionToken
    };
  }

  async invokeAgent(request: AWSAIAgentRequest): Promise<AWSAIAgentResponse> {
    try {
      // Check if we have a session for this ticker
      const tickerSession = this.sessionsByTicker.get(request.symbol);
      
      // Generate or reuse session ID
      if (!this.runtimeSessionId) {
        // Try to use ticker-specific session first
        if (tickerSession) {
          this.runtimeSessionId = tickerSession;
          logger.debug('AWS AI Agent - Reusing ticker session:', this.runtimeSessionId);
        } else {
          this.runtimeSessionId = this.generateSessionId();
          logger.debug('AWS AI Agent - Generated new session:', this.runtimeSessionId);
        }
      }

      // Store session for this ticker
      this.sessionsByTicker.set(request.symbol, this.runtimeSessionId);

      logger.debug('AWS AI Agent - Session ID:', this.runtimeSessionId);
      logger.debug('AWS AI Agent - Session ID Length:', this.runtimeSessionId.length);

      const credentials = this.getCredentials();
      
      // Create BedrockAgentCore client with credentials
      const client = new BedrockAgentCoreClient({
        region: 'us-east-1',
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken
        }
      });

      // Prepare the input text
      const inputText = JSON.stringify(request);
      const payload = new TextEncoder().encode(inputText);

      // Create the command input following the AWS example
      const input = {
        runtimeSessionId: this.runtimeSessionId, // Must be 33+ chars
        agentRuntimeArn: "arn:aws:bedrock-agentcore:us-east-1:550939689230:runtime/ai_stock_analysis_agent_dev-cWteoyGiGo",
        qualifier: "DEFAULT", // Optional
        payload: payload // Required
      };

      logger.debug('AWS AI Agent - Invoking with BedrockAgentCoreClient');
      logger.debug('AWS AI Agent - Input:', {
        runtimeSessionId: input.runtimeSessionId,
        agentRuntimeArn: input.agentRuntimeArn,
        qualifier: input.qualifier,
        payloadLength: payload.length
      });

      // Create and send the command
      const command = new InvokeAgentRuntimeCommand(input);
      const response = await client.send(command);

      logger.debug('AWS AI Agent - Response received:', response);

      // Transform the response to string
      if (response.response) {
        const textResponse = await response.response.transformToString();
        logger.debug('AWS AI Agent - Text response length:', textResponse.length);

        // Try to parse as JSON, or create a structured response
        try {
          const parsedResponse = JSON.parse(textResponse);
          return parsedResponse as AWSAIAgentResponse;
        } catch (parseError) {
          // If it's not JSON, create a structured response with comprehensive_analysis
          return {
            result: {
              comprehensive_analysis: textResponse,
              symbols: [request.symbol],
              timeframe: request.timeframe,
              analysis_depth: request.analysis_depth,
              analysis_type: 'auto',
              confidence: 0.5,
              processing_time_ms: 0,
              timestamp: new Date().toISOString()
            }
          };
        }
      } else {
        throw new Error('No response received from AWS AI Agent');
      }
    } catch (error) {
      logger.error('AWS AI Agent invocation failed:', error);
      throw error;
    }
  }

  // Reset session ID to force a new session
  resetSession(): void {
    this.runtimeSessionId = null;
  }

  // Reset session for a specific ticker
  resetSessionForTicker(ticker: string): void {
    this.sessionsByTicker.delete(ticker);
    // If current session matches this ticker's session, clear it
    const tickerSession = this.sessionsByTicker.get(ticker);
    if (this.runtimeSessionId === tickerSession) {
      this.runtimeSessionId = null;
    }
  }

  // Get current session ID
  getSessionId(): string | null {
    return this.runtimeSessionId;
  }

  // Get session ID for a specific ticker
  getSessionIdForTicker(ticker: string): string | null {
    return this.sessionsByTicker.get(ticker) || null;
  }

  // Set session for a ticker (useful when loading from cache)
  setSessionForTicker(ticker: string, sessionId: string): void {
    this.sessionsByTicker.set(ticker, sessionId);
    this.runtimeSessionId = sessionId;
  }

  // Send a follow-up message using the existing session
  async sendFollowUpMessage(message: string, symbol: string): Promise<AWSAIAgentResponse> {
    // If no session exists (e.g., loaded from cache), create one
    // The invokeAgent method will automatically create a session if needed
    if (!this.runtimeSessionId) {
      logger.debug('No active session found. Creating new session for follow-up message.');
    }

    const request: AWSAIAgentRequest = {
      prompt: message,
      symbol: symbol,
      timeframe: "1y",
      analysis_depth: "detailed"
    };

    return this.invokeAgent(request);
  }
}

export const awsAIAgentService = AWSAIAgentService.getInstance();